import { ContentAdd, ContentDelete, MenuSearch } from '@fs/zion-icon'
import { Button, DangerButton, Row, Select, TextField, useOverlay, Paragraph } from '@fs/zion-ui'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { deleteMatchTerm, getMatchTermsFiltered, postMatchTerm } from '../../../../services/sensitiveService'
import { getUser } from '../../../../services/userService'
import { getIdElement } from '../../../../util/StringUtil'
import { useKeypress } from '../../../../util/Utils'
import { UserLink } from '../../../app/Links'
import MatchTermsFilteredDialog from './MatchTermsFilteredDialog'

const MatchTermSearch = ({ category, lang, setLang, langs }) => {
  const { t } = useTranslation()
  const matchTermsFilteredOverlay = useOverlay()
  const { ok, error } = useNotifications()
  const [matchTerm, setMatchTerm] = useState()
  const [term, setTerm] = useState()
  const [filteredMatchTerms, setFilteredMatchTerms] = useState([])
  const [contactName, setContactName] = useState()
  const searchBtnDisabled = !term || !lang

  const termOnChange = (text) => {
    setMatchTerm()
    setContactName()
    setTerm(text)
  }

  const searchBtnOnClickHandler = useCallback(async () => {
    try {
      const matchTermResponse = await getMatchTermsFiltered(term.trim(), category, lang)
      const matchTermsFiltered = matchTermResponse.data
      if (matchTermsFiltered.length === 0) {
        setMatchTerm({ term: null })
      }
      if (matchTermsFiltered.length > 1 || matchTermResponse.data[0].term.toLowerCase() !== term.toLowerCase().trim()) {
        setFilteredMatchTerms(matchTermsFiltered)
        matchTermsFilteredOverlay.open()
      } else {
        if (matchTermResponse.data[0].lastUpdaterCisId) {
          try {
            const userResponse = await getUser(matchTermResponse.data[0].lastUpdaterCisId, 'cisId')
            setContactName(userResponse.data?.identity?.users[0].contactName)
          } catch (err) {
            setContactName()
          }
        }
        setMatchTerm(matchTermResponse.data[0])
      }
    } catch (err) {
      // Notify admin of error
    }
  }, [category, lang, matchTermsFilteredOverlay, term])

  const clearBtnOnClickHandler = () => {
    window.scrollTo({ top: 0, insetInlineStart: 0, behavior: 'smooth' })
    setTerm()
    setMatchTerm()
    setContactName()
  }

  const addBtnOnClickHandler = () => {
    window.scrollTo({ top: 0, insetInlineStart: 0, behavior: 'smooth' })
    const body = {
      category,
      term,
      lang,
    }
    postMatchTerm(body)
      .then(() => {
        ok(
          t('tools.lists.notifications.addSuccess', '{matchTerm} successfully added', {
            matchTerm: term,
          })
        )
        clearBtnOnClickHandler()
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 409:
            error(
              t(
                'tools.errors.termExists',
                `Error adding term {term} to the {category} category list: term already exists. {message}`,
                {
                  term,
                  category,
                  message: err.message || '',
                }
              )
            )
            return
          default:
            error(
              t('tools.errors.addingTerm', `Error adding term {term} to the {category} category list: {message}`, {
                term,
                category,
                message: err.message || '',
              })
            )
            break
        }
      })
  }

  const deleteBtnOnClickHandler = () => {
    window.scrollTo({ top: 0, insetInlineStart: 0, behavior: 'smooth' })
    deleteMatchTerm(matchTerm.id)
      .then(() => {
        clearBtnOnClickHandler()
        ok(
          t('tools.lists.notifications.deleteSuccess', '{matchTerm} successfully deleted', {
            matchTerm: term,
          })
        )
      })
      .catch((err) => {
        error(
          t('tools.errors.deletingTerm', `Error deleting term {term} for the {category} category list: {message}`, {
            term,
            category,
            message: err.message || '',
          }),
          err
        )
      })
  }

  const onEnterKeyPress = useCallback(() => {
    if (!searchBtnDisabled) {
      searchBtnOnClickHandler()
    }
  }, [searchBtnDisabled, searchBtnOnClickHandler])

  useKeypress('Enter', onEnterKeyPress)
  return (
    <>
      <Row guttersY="none">
        <TextField
          id="term"
          label={getSearchLabel(category, t)}
          type="text"
          value={matchTerm?.term ? matchTerm.term : term}
          onChange={(e) => termOnChange(e.target.value)}
          validMessage={getValidMessage(category, matchTerm, t)}
          errorMessage={getErrorMessage(category, matchTerm, t)}
        />
        {matchTerm && matchTerm.lastUpdaterCisId && (
          <Paragraph alignY="nextToInput" size="sm">
            <strong>{`${t('cisId', 'CIS ID')}: `}</strong>
            {getIdElement(matchTerm.lastUpdaterCisId)}
          </Paragraph>
        )}
        {contactName && matchTerm?.lastUpdaterCisId && (
          <Paragraph alignY="nextToInput" size="sm">
            <strong>{`${t('contactName', 'Contact Name')}: `}</strong>
            {contactName && matchTerm?.lastUpdaterCisId && (
              <UserLink userId={matchTerm.lastUpdaterCisId} userName={contactName} />
            )}
          </Paragraph>
        )}
        {matchTerm && matchTerm.updateDate && (
          <Paragraph alignY="nextToInput" size="sm">
            <strong>{`${t('lastModified', 'Last Modified')}: `}</strong>
            {matchTerm.updateDate}
          </Paragraph>
        )}
      </Row>
      <Row>
        <Select
          id="lang"
          placeholder={t('language.select', 'Select Language')}
          label={t('language.label', 'Language')}
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          options={langs}
        />
      </Row>
      <Row>
        <Button Icon={MenuSearch} onClick={searchBtnOnClickHandler} emphasis="high" disabled={searchBtnDisabled}>
          {t('action.search', 'Search')}
        </Button>
        <Button onClick={clearBtnOnClickHandler} emphasis="medium" disabled={searchBtnDisabled}>
          {t('action.clear', 'Clear')}
        </Button>
        {term && lang && (!matchTerm || matchTerm.term === null) && (
          <Button Icon={ContentAdd} onClick={addBtnOnClickHandler} emphasis="medium">
            {t('action.add', 'Add')}
          </Button>
        )}
        {matchTerm?.term && (
          <DangerButton Icon={ContentDelete} onClick={deleteBtnOnClickHandler}>
            {t('action.delete', 'Delete')}
          </DangerButton>
        )}
      </Row>
      <MatchTermsFilteredDialog
        category={category}
        matchTerms={filteredMatchTerms}
        setMatchTerm={setMatchTerm}
        setContactName={setContactName}
        {...matchTermsFilteredOverlay}
      />
    </>
  )
}

export default MatchTermSearch

function getSearchLabel(category, t) {
  switch (category) {
    case 'christian-religion-names':
    case 'deity':
    case 'fictitious':
    case 'mythological':
    case 'non-holocaust-countries':
    case 'primary-death-camp-names':
    case 'profanity':
    case 'religious-titles':
    case 'secondary-death-camp-names':
      return t('tools.lists.labels.term', 'Term')
    case 'holocaust-contributor-ids':
      return t('contributorId', 'Contributor ID')
    default:
      return t('tools.lists.labels.term', 'Term')
  }
}

function getValidMessage(category, matchTerm, t) {
  switch (category) {
    case 'christian-religion-names':
    case 'deity':
    case 'fictitious':
    case 'mythological':
    case 'non-holocaust-countries':
    case 'primary-death-camp-names':
    case 'profanity':
    case 'religious-titles':
    case 'secondary-death-camp-names':
      return matchTerm?.term ? t('tools.lists.notifications.foundTerm', 'Found Term') : ''
    case 'holocaust-contributor-ids':
      return matchTerm?.term ? t('tools.lists.notifications.foundContributor', 'Found Contributor') : ''
    default:
      return matchTerm?.term ? t('tools.lists.notifications.foundTerm', 'Found Term') : ''
  }
}

function getErrorMessage(category, matchTerm, t) {
  switch (category) {
    case 'christian-religion-names':
    case 'deity':
    case 'fictitious':
    case 'mythological':
    case 'non-holocaust-countries':
    case 'primary-death-camp-names':
    case 'profanity':
    case 'religious-titles':
    case 'secondary-death-camp-names':
      return matchTerm && matchTerm.term === null ? t('tools.lists.notifications.termNotFound', 'Term Not Found') : ''
    case 'holocaust-contributor-ids':
      return matchTerm && matchTerm.term === null
        ? t('tools.lists.notifications.contributorNotFound', 'Contributor Not Found')
        : ''
    default:
      return matchTerm && matchTerm.term === null ? t('tools.lists.notifications.termNotFound', 'Term Not Found') : ''
  }
}
